import type { PDFDocumentProxy } from "pdfjs-dist";

/**
 * Pages of a PDF drawn onto canvases, for slide thumbnails.
 *
 * pdf.js is loaded on first use, so nothing else pays for it, and its worker
 * is bundled as an asset. The document is opened with range requests only:
 * a brochure is 10–35 MB, and a thumbnail needs a page's worth of it. S3
 * answers ranged GETs from any origin, so the signed URL works as is.
 *
 * Kept in its own module so tests replace it whole; jsdom has no canvas.
 */

type PdfJs = typeof import("pdfjs-dist");

let library: Promise<PdfJs> | null = null;

const pdfjs = () => {
  if (!library) {
    library = import("pdfjs-dist/legacy/build/pdf").then((module) => {
      const lib = module as unknown as PdfJs;
      lib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/legacy/build/pdf.worker.min.js", import.meta.url,
      ).toString();
      return lib;
    });
  }
  return library;
};

export const openPdf = async (url: string): Promise<PDFDocumentProxy> => {
  const lib = await pdfjs();
  return lib.getDocument({ url, disableAutoFetch: true, disableStream: true }).promise;
};

/**
 * Draw page `number` (1-based) at `width` CSS pixels, sharp on a retina
 * screen. Returns a cancel: pdf.js refuses two renders on one canvas, so a
 * canvas redrawn before it finished has to stop the first.
 */
export const drawPage = (
  doc: PDFDocumentProxy, number: number, canvas: HTMLCanvasElement, width: number,
) => {
  let cancelled = false;
  let task: { cancel: () => void } | null = null;
  const done = (async () => {
    const page = await doc.getPage(number);
    if (cancelled) return;
    const base = page.getViewport({ scale: 1 });
    const ratio = window.devicePixelRatio || 1;
    const viewport = page.getViewport({ scale: (width / base.width) * ratio });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    // Height is left to the caller's aspect-ratio, so a drawn page takes
    // exactly the space its placeholder held.
    canvas.style.width = `${width}px`;
    const context = canvas.getContext("2d");
    if (!context) return;
    const render = page.render({ canvasContext: context, viewport });
    task = render;
    await render.promise;
  })().catch(() => undefined);   // a cancelled render rejects; nothing to say
  return {
    done,
    cancel: () => { cancelled = true; task?.cancel(); },
  };
};
